import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BinomialDistributionCalculator = () => {
  const [n, setN] = useState('');
  const [p, setP] = useState('');
  const [k, setK] = useState('');
  const [probability, setProbability] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleNChange = (e) => {
    setN(e.target.value);
    setError('');
  };

  const handlePChange = (e) => {
    setP(e.target.value);
    setError('');
  };

  const handleKChange = (e) => {
    setK(e.target.value);
    setError('');
  };

  const calculateProbability = () => {
    const nValue = parseInt(n);
    const pValue = parseFloat(p);
    const kValue = parseInt(k);

    if (isNaN(nValue) || nValue <= 0) {
      setError('Please enter a valid positive integer for n (number of trials)');
      return;
    }

    if (isNaN(pValue) || pValue < 0 || pValue > 1) {
      setError('Please enter a valid probability (p) between 0 and 1');
      return;
    }

    if (isNaN(kValue) || kValue < 0 || kValue > nValue) {
      setError('Please enter a valid integer for k (number of successes) between 0 and n');
      return;
    }

    // Calculate binomial coefficient (n choose k)
    const binomialCoefficient = (n, k) => {
      let result = 1;
      for (let i = 1; i <= k; i++) {
        result *= (n + 1 - i) / i;
      }
      return result;
    };

    // Calculate binomial probability
    const binomial = (n, k, p) => {
      return binomialCoefficient(n, k) * Math.pow(p, k) * Math.pow(1 - p, n - k);
    };

    const prob = binomial(nValue, kValue, pValue);
    const calculationSteps = [
      'Step 1: Prepare the values',
      `Number of Trials (n): ${nValue}`,
      `Probability of Success (p): ${pValue}`,
      `Number of Successes (k): ${kValue}`,
      'Step 2: Calculate Binomial Coefficient',
      `C(n, k) = ${binomialCoefficient(nValue, kValue)}`,
      'Step 3: Calculate Binomial Probability',
      `P(X = k) = C(n, k) * p^k * (1-p)^(n-k)`,
      `P(X = ${kValue}) = ${binomialCoefficient(nValue, kValue)} * ${pValue}^${kValue} * ${1 - pValue}^${nValue - kValue}`,
      `P(X = ${kValue}) = ${prob.toFixed(6)} (or ${prob * 100}%)`
    ];

    setProbability({
      value: prob.toFixed(6),
      calculationSteps
    });

    // Generate chart data
    const generateBinomialDistribution = (n, p, maxK = n) => {
      const data = [];
      for (let k = 0; k <= maxK; k++) {
        data.push({
          k,
          probability: binomial(n, k, p)
        });
      }
      return data;
    };

    const distributionData = generateBinomialDistribution(nValue, pValue);
    setChartData({
      labels: distributionData.map(d => d.k),
      datasets: [
        {
          label: 'Binomial Distribution',
          data: distributionData.map(d => d.probability),
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgb(75, 192, 192)',
          borderWidth: 1
        }
      ]
    });
  };

  const exampleData = {
    n: 10,
    p: 0.5,
    k: 5,
    prob: 0.24609375
  };

  return (
    <>
      <Helmet>
        <title>Binomial Distribution Calculator - Statistics</title>
        <meta name="description" content="Calculate probabilities using the Binomial Distribution. Use our interactive calculator to find probabilities based on number of trials, probability of success, and number of successes." />
        <meta name="keywords" content="binomial distribution calculator, statistics calculator, probability calculator" />
        <meta property="og:title" content="Binomial Distribution Calculator - Statistics" />
        <meta property="og:description" content="Calculate probabilities using the Binomial Distribution. Use our interactive calculator to find probabilities based on number of trials, probability of success, and number of successes." />
        <meta name="twitter:title" content="Binomial Distribution Calculator - Statistics" />
        <meta name="twitter:description" content="Calculate probabilities using the Binomial Distribution. Use our interactive calculator to find probabilities based on number of trials, probability of success, and number of successes." />
      </Helmet>

      <div className="statistics-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">📊</div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Binomial Distribution Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Statistics Calculator</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            Calculate probabilities using the Binomial Distribution. This calculator helps you find probabilities based on the number of trials, probability of success, and number of successes.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
        >
          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="n">
              Number of Trials (n):
            </label>
            <input
              type="text"
              id="n"
              name="n"
              value={n}
              onChange={handleNChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter the number of trials (n)"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="p">
              Probability of Success (p):
            </label>
            <input
              type="text"
              id="p"
              name="p"
              value={p}
              onChange={handlePChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter the probability of success (p)"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="k">
              Number of Successes (k):
            </label>
            <input
              type="text"
              id="k"
              name="k"
              value={k}
              onChange={handleKChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter the number of successes (k)"
            />
          </div>

          <button
            onClick={calculateProbability}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
          >
            Calculate Probability
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}

          {probability && (
            <div className="results-section mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Probability</h4>
                  <div className="space-y-2">
                    <p className="flex justify-between">
                      <span>P(X = k)</span>
                      <span className="font-medium">{probability.value}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-12 mt-12">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Binomial Distribution</h4>
                  <div className="h-[400px]">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          x: {
                            title: {
                              display: true,
                              text: 'Number of Successes (k)'
                            }
                          },
                          y: {
                            title: {
                              display: true,
                              text: 'Probability'
                            },
                            beginAtZero: true
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Binomial Distribution'
                          },
                          legend: {
                            display: true,
                            position: 'bottom'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-12 bg-gray-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
                <div className="space-y-4">
                  {probability.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Example Calculation <span className="ml-2 text-2xl">📝</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <p className="text-gray-700 mb-6">
              Here's an example calculation for n = 10, p = 0.5, and k = 5:
            </p>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold mb-4">Sample Data:</h4>
              <ul className="list-disc pl-8 space-y-2">
                <li>Number of Trials (n): {exampleData.n}</li>
                <li>Probability of Success (p): {exampleData.p}</li>
                <li>Number of Successes (k): {exampleData.k}</li>
                <li>Probability: {exampleData.prob}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            How to Use Binomial Distribution Calculator <span className="ml-2 text-2xl">📋</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <p className="text-gray-700 mb-6">
              Follow these steps to use the Binomial Distribution Calculator effectively:
            </p>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Gather Data</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Collect the number of trials (n)</li>
                  <li>Identify the probability of success (p)</li>
                  <li>Determine the number of successes (k)</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Data</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Input the n, p, and k values into the calculator</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Review the calculated probability</li>
                  <li>Use the binomial distribution chart for visual analysis</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="space-y-6">
              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">What is the Binomial Distribution?</h3>
                <p className="text-gray-600">
                  The Binomial Distribution is a discrete probability distribution that models the number of successes in a fixed number of independent Bernoulli trials.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">What is n in this context?</h3>
                <p className="text-gray-600">
                  n represents the number of trials or experiments conducted.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">What is p in this context?</h3>
                <p className="text-gray-600">
                  p represents the probability of success in each trial.
                </p>
              </div>

              <div className="faq-item">
                <h3 className="text-lg font-semibold mb-2">What is k in this context?</h3>
                <p className="text-gray-600">
                  k represents the number of successes you want to calculate the probability for.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Features & Benefits <span className="ml-2 text-2xl">✨</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <div className="text-4xl mb-4">📊</div>
                <h3 className="text-lg font-semibold mb-2">Visual Analysis</h3>
                <p className="text-gray-600">Interactive binomial distribution chart for easy visualization of probability calculations</p>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-lg font-semibold mb-2">Precise Calculations</h3>
                <p className="text-gray-600">Accurate probability calculations with step-by-step breakdown</p>
              </div>
              <div className="text-center p-6 bg-yellow-50 rounded-lg">
                <div className="text-4xl mb-4">🔄</div>
                <h3 className="text-lg font-semibold mb-2">Unit Conversion</h3>
                <p className="text-gray-600">Automatic handling of data format conversions</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <div className="text-4xl mb-4">📚</div>
                <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
                <p className="text-gray-600">Detailed explanations of binomial distribution concepts</p>
              </div>
              <div className="text-center p-6 bg-pink-50 rounded-lg">
                <div className="text-4xl mb-4">⚙️</div>
                <h3 className="text-lg font-semibold mb-2">User-Friendly</h3>
                <p className="text-gray-600">Intuitive interface with clear input fields</p>
              </div>
              <div className="text-center p-6 bg-orange-50 rounded-lg">
                <div className="text-4xl mb-4">🔄</div>
                <h3 className="text-lg font-semibold mb-2">Real-time Updates</h3>
                <p className="text-gray-600">Instant calculation results as you type</p>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Understanding Binomial Distribution <span className="ml-2 text-2xl">📊</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <p className="text-gray-700 mb-6">
              The Binomial Distribution is a fundamental concept in statistics that helps us understand the probability of successes in a fixed number of trials. Here's why it's important:
            </p>
            <ul className="list-none pl-8 space-y-4">
              <li className="flex items-center">
                <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
                <span className="font-medium">Trial Analysis:</span> Analyzing the outcomes of repeated trials
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
                <span className="font-medium">Success Probability:</span> Calculating the probability of specific numbers of successes
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
                <span className="font-medium">Business Applications:</span> Used in various fields like quality control, genetics, and social sciences
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
                <span className="font-medium">Statistical Inference:</span> Making predictions and decisions based on trial outcomes
              </li>
            </ul>
            <p className="italic text-gray-500 mt-6">
              Note: Always double-check your data and calculations to ensure accuracy and avoid misleading results.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default BinomialDistributionCalculator;
